import type { Command } from '@/core/Command';
import type { EnvironmentManager } from '@/environment/EnvironmentManager';
import type { EnvironmentState } from '@/environment/EnvironmentState';

/**
 * An environment change as one undoable step.
 *
 * The environment tray deliberately does *not* go through the history: dragging the time of day is a
 * viewing decision, and burying an afternoon's planting under two hundred sun-position entries would
 * make undo useless. The wand is the opposite case — "show me this in late October" arrives as one
 * sentence alongside a planting change, and the two have to come back together or not at all.
 */
export class SetEnvironmentCommand implements Command {
  readonly label: string;
  readonly kind = 'environment' as const;
  private readonly before: EnvironmentState;
  private readonly after: EnvironmentState;

  constructor(
    private readonly environment: EnvironmentManager,
    after: EnvironmentState,
    label = 'Change environment',
  ) {
    this.label = label;
    this.before = clone(environment.state);
    this.after = clone(after);
  }

  execute(): void {
    this.environment.load(clone(this.after));
  }

  undo(): void {
    this.environment.load(clone(this.before));
  }
}

function clone(state: EnvironmentState): EnvironmentState {
  return {
    ...state,
    time: { ...state.time },
    date: { ...state.date },
  };
}
